// Pure chain logic for the Theory of Change navigator.
//
// The navigator walks the org's own causal pathway one level at a time:
// doelgroep → activiteit → mechanisme → effect. The external framing
// (maatschappelijk probleem + impactdoelen) is NOT a step — it's derived context
// that surrounds the whole chain (rendered as bands above/below the navigator).
// See docs/features/toc-2.0-roadmap.md F2.
//
// Link arrays reference entities by `ID` (not docid) — same pattern as the rest
// of the TOC (see src/helpers/tocHelpers/hasCompleteTOCChain.js):
//
//   doel → act   via Activity.Targetgroup[]      (contains stakeholder ID)
//   act  → mech  via Mechanism.Activity[]        (contains activity ID)
//   mech → eff   via Mechanism.Effect[]          (contains effect ID)
//
// doelgroep is the free root; effect is the terminal.

export const STEP_ORDER = ['doel', 'act', 'mech', 'eff']

// How to read the title out of each collection's raw doc.
const TITLE_FIELD = {
  doel: 'Organisation',
  act: 'Activity',
  mech: 'Title',
  eff: 'Effect',
}

/**
 * @typedef {object} NavigatorNode
 * @property {string} id - The entity ID (link key).
 * @property {'doel'|'act'|'mech'|'eff'|'impactcontext'} step - Which chain step.
 * @property {string} title - Display title resolved from the step's title field.
 * @property {object} raw - The original Firestore doc.
 */

const toNodes = (list, step) =>
  (Array.isArray(list) ? list : [])
    .filter((raw) => raw && raw.ID)
    .map((raw) => ({ id: raw.ID, step, title: raw[TITLE_FIELD[step]] || '', raw }))

/**
 * Normalise the chain collections into one navigator model. `impactcontext` is a
 * single synthetic terminal node (derived, not a collection).
 *
 * @param {object} data
 * @param {Array} [data.stakeholders] - Stakeholders (doelgroepen) docs.
 * @param {Array} [data.activities] - Activities docs.
 * @param {Array} [data.mechanisms] - Mechanisms docs.
 * @param {Array} [data.effects] - OutputEffects docs.
 * @returns {{ byStep: Record<string, NavigatorNode[]>, byId: Map<string, NavigatorNode> }}
 */
export const buildNavigatorModel = (data = {}) => {
  const byStep = {
    doel: toNodes(data.stakeholders, 'doel'),
    act: toNodes(data.activities, 'act'),
    mech: toNodes(data.mechanisms, 'mech'),
    eff: toNodes(data.effects, 'eff'),
  }

  const byId = new Map()
  STEP_ORDER.forEach((step) => byStep[step].forEach((node) => byId.set(node.id, node)))

  return { byStep, byId }
}

/**
 * Look up a node by ID.
 *
 * @param {{ byId: Map<string, NavigatorNode> }} model
 * @param {string} id
 * @returns {NavigatorNode|null}
 */
export const getNode = (model, id) => (model && model.byId.get(id)) || null

/**
 * Build a navigator path that places `id` at exactly `level` (its own step
 * index), keeping the upstream chain and padding any missing upstream slots with
 * `''`. Anything below `level` is dropped.
 *
 * Guards the "effect shows as mechanisme" bug: appending an effect with
 * `path.slice(0, 3).concat(id)` only lands it at index 3 when the path already
 * has three entries. When no mechanism (or activity) is selected, the shorter
 * path made the effect collapse into the mechanism slot (index 2), so the
 * breadcrumb column and detail banner — which read the step label from the path
 * POSITION — framed the effect as a "Werkzaam mechanisme".
 *
 * @param {string[]} [path] - Current chain of node IDs (contiguous from 0).
 * @param {number} level - 0-based target index into STEP_ORDER.
 * @param {string} id - The node ID to place at `level`.
 * @returns {string[]} A new path of length `level + 1` with `id` at `level`.
 */
export const placeAtLevel = (path, level, id) => {
  const next = []
  for (let i = 0; i < level; i++) next[i] = (path && path[i]) || ''
  next[level] = id
  return next
}

const idsOf = (nodes) => nodes.map((n) => n.id)

const includesId = (arr, id) => Array.isArray(arr) && arr.includes(id)

/**
 * Which node IDs are selectable at a given chain level, given the choices made
 * for the earlier levels.
 *
 * @param {number} level - 0-based index into STEP_ORDER.
 * @param {string[]} path - Chosen node IDs per earlier level (contiguous from 0).
 * @param {{ byStep: Record<string, NavigatorNode[]> }} model
 * @returns {string[]} The available node IDs at this level (possibly empty).
 */
export const optionsAt = (level, path, model) => {
  if (!model) return []
  const { byStep } = model

  switch (STEP_ORDER[level]) {
    case 'doel':
      // Free root — every doelgroep.
      return idsOf(byStep.doel)
    case 'act': {
      const doelId = path[0]
      if (!doelId) return []
      return idsOf(byStep.act.filter((a) => includesId(a.raw.Targetgroup, doelId)))
    }
    case 'mech': {
      const actId = path[1]
      if (!actId) return []
      return idsOf(byStep.mech.filter((m) => includesId(m.raw.Activity, actId)))
    }
    case 'eff': {
      const mech = getNode(model, path[2])
      if (!mech) return []
      return idsOf(byStep.eff.filter((e) => includesId(mech.raw.Effect, e.id)))
    }
    default:
      return []
  }
}
